<template>
  <div>
    <funnels-item-view v-if="mode === 'item'"
      :rawData="funnelData"/>
    <funnels-session-list v-else
      :rawData="funnelData"/>
    <dimmed v-if="isLoading && mode === 'item'">
      <ui-loading-circle :descText="'loadingDot'" :applyBackground="true" />
    </dimmed>
  </div>
</template>

<script>
import store from '@/store'
import { mapState, mapMutations } from 'vuex'
import { getReport } from '@/api/funnel'
import FunnelsItemView from '@/components/funnels/FunnelsItemView'
import FunnelsSessionList from '@/components/funnels/FunnelsSessionList'
import { parse, format, isBefore, isValid, startOfDay, addDays } from 'date-fns'
import { tzOffsetDate, dateFormatTimezone } from '@/libs/utils'
import Dimmed from '@/components/ui/Dimmed'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'

export default {
  name: 'FunnelsView',
  components: {
    FunnelsItemView,
    FunnelsSessionList,
    UiLoadingCircle,
    Dimmed
  },
  data () {
    return {
      mode: 'item',
      isLoading: false
    }
  },
  computed: {
    ...mapState([
      'reportPeriod',
      'funnelData'
    ])
  },
  watch: {
    reportPeriod (newValue, oldValue) {
      if (newValue.isFunnelMinSet === oldValue.isFunnelMinSet) { // if just min date has changed, pass
        this.loadData()
      }
    }
  },
  methods: {
    ...mapMutations([
      'setFunnelData',
      'setReportPeriod'
    ]),
    loadData () {
      const fid = this.$route.params.fid
      const period = this.reportPeriod
      const params = {
        fid,
        startDate: format(period.start,'YYYY-MM-DD'),
        endDate: format(period.end, 'YYYY-MM-DD')
      }

      this.isLoading = true
      getReport(params).then(data => {
        this.setFunnelData(data.funnel)
        this.mode = this.$route.name === 'funnel.view' ? 'item' : 'sessionList'
        this.isLoading = false
      }).catch(err => {
        console.error(err)
      })
    }
  },
  beforeRouteEnter (to, from, next) {
    const fid = to.params.fid

    if (!fid) {
      alert('no fid')
      return next(false)
    }

    if (!['funnel.view', 'funnel.sessionList'].includes(from.name)) {
      const period = store.state.reportPeriod
      const userData = store.state.userData
      const today = startOfDay(tzOffsetDate(new Date(), userData.timezone))

      const params = {
        fid,
        startDate: format(period.start, 'YYYY-MM-DD'),
        endDate: format(period.end, 'YYYY-MM-DD')
      }

      getReport(params).then(data => {
        const funnelRegDate = dateFormatTimezone(parse(data.funnel.regDate), userData.timezoneName, 'YYYY-MM-DD')
        const funnelRegDateValid = isValid(new Date(funnelRegDate))
        let lastDate = null
        for (let report of data.funnel.funnelList) {
          if (!lastDate || lastDate > report.stopDate) {
            lastDate = report.stopDate
          }
        }

        const now = tzOffsetDate(new Date(), userData.timezone)
        let endDate = null
        if (lastDate) {
          if (funnelRegDateValid && isBefore(new Date(lastDate), new Date(funnelRegDate))) {
            endDate = funnelRegDate
          } else {
            endDate = lastDate
          }
        } else {
          endDate = format(today, 'YYYY-MM-DD')
        }
        let startDate = addDays(parse(endDate), -6)
        if (funnelRegDateValid && isBefore(startDate, new Date(funnelRegDate))) {
          startDate = funnelRegDate
        } else {
          startDate = format(startDate, 'YYYY-MM-DD')
        }

        store.commit('setFunnelData', data.funnel)
        store.commit('setReportPeriod', {
          ...period,
          min: (funnelRegDateValid && !period.isFunnelMinSet ? funnelRegDate : period.min),
          funnelMin: (funnelRegDateValid && !period.isFunnelMinSet ? period.min : funnelRegDate),
          max: endDate,
          start: startDate,
          end: endDate,
          isFunnelMinSet: true
        })
        next(vm => {
          vm.mode = to.name === 'funnel.view' ? 'item' : 'sessionList'
          vm.loadData()
        })
      }).catch(err => {
        console.error(err)
        next(false)
      })
    } else {
      next(vm => {
        vm.mode = to.name === 'funnel.view' ? 'item' : 'sessionList'
        vm.loadData()
      })
    }
  },
  beforeDestroy () {
    const funnelRegDateValid = isValid(new Date(this.reportPeriod.funnelMin))
    this.setReportPeriod({
      ...this.reportPeriod,
      funnelMin: (funnelRegDateValid ? this.reportPeriod.min : this.reportPeriod.funnelMin),
      min: (funnelRegDateValid ? this.reportPeriod.funnelMin : this.reportPeriod.min),
      isFunnelMinSet: false
    })
  }
}
</script>

<style>

</style>
